import { createRoot } from 'react-dom/client';
import { Theme } from '@classify/react-core-components/Theme';
import type { ClientApi } from '@openclassrooms/sdk-web';
import CourseLinkCard, { CourseLinkCardData, CourseLinkCardTranslations } from '../components/CourseLinkCard';
const COURSE_PATH_REGEXP = /^\/(?:[a-z]{2}\/)??courses\/(\d+)(?:-[^/]+)?\/?$/;
type CourseResponse = CourseLinkCardData | {
  body?: CourseLinkCardData;
};
const isWhitespaceTextNode = (node: ChildNode) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();
const hasCourseCardData = (course?: CourseLinkCardData | null): course is CourseLinkCardData => Boolean(course?.title && course?.illustration && course?.duration);
const parseCourseCardTranslations = (domContainer: HTMLElement): CourseLinkCardTranslations => {
  const {
    courseCardTranslations
  } = domContainer.dataset;
  if (!courseCardTranslations) {
    return {};
  }
  try {
    return JSON.parse(courseCardTranslations) as CourseLinkCardTranslations;
  } catch {
    return {};
  }
};
const getCourseFromResponse = (response: CourseResponse): CourseLinkCardData | undefined => 'body' in response && response.body ? response.body : response;
export const extractCourseIdFromUrl = (url: URL): number | null => {
  if (url.search || url.hash) {
    return null;
  }
  const match = url.pathname.match(COURSE_PATH_REGEXP);
  if (!match) {
    return null;
  }
  return Number(match[1]);
};
export const isStandaloneCourseLink = (linkElement: HTMLAnchorElement): boolean => {
  const parentElement = linkElement.parentElement;
  if (!parentElement) {
    return false;
  }
  return Array.from(parentElement.childNodes).every(childNode => childNode === linkElement || isWhitespaceTextNode(childNode));
};
async function renderCourseLink(domContainer: HTMLElement, linkElement: HTMLAnchorElement, client: ClientApi, index: number) {
  const courseId = extractCourseIdFromUrl(new URL(linkElement.href));
  const parentElement = linkElement.parentElement;
  if (!courseId || !parentElement?.parentNode) {
    return;
  }
  const response = (await client.helpers.learningContent.courses.get(courseId)) as CourseResponse;
  const course = getCourseFromResponse(response);
  if (!hasCourseCardData(course) || !linkElement.isConnected) {
    return;
  }
  const rootContainer = document.createElement('div');
  parentElement.parentNode.replaceChild(rootContainer, parentElement);
  const root = createRoot(rootContainer);
  root.render(<Theme productionPrefix={`course_link_${index}`}>
      <CourseLinkCard course={course} href={linkElement.href} translations={parseCourseCardTranslations(domContainer)} />
    </Theme>);
}
export default function handleCourseLinks(domContainer: HTMLElement, client?: ClientApi) {
  if (!client) {
    return null;
  }
  const links = Array.from(domContainer.getElementsByTagName('a'));
  if (!links.length) {
    return null;
  }
  links.filter(linkElement => isStandaloneCourseLink(linkElement)).forEach((linkElement, index) => {
    renderCourseLink(domContainer, linkElement, client, index).catch(() => undefined);
  });
  return null;
}